import React from 'react';
import { WebsiteDesignProps } from './types';
import { Waves, Droplet, Fish, Anchor } from 'lucide-react';
import { Button } from "@/components/ui/button";

const OceanicDepths: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Blue Horizon Academy";

    return (
        <div className="bg-gradient-to-b from-[#e0f7fa] to-[#006064] min-h-screen font-sans text-[#006064]">
            <nav className="p-8 flex justify-between items-center relative z-20">
                <div className="text-3xl font-bold tracking-tighter flex items-center gap-2 text-[#00838f]">
                    <Anchor size={28} /> {schoolName}
                </div>
                <div className="flex gap-6 font-medium text-[#006064]">
                    <a href="#" className="hover:text-[#00acc1] transition-colors">Academics</a>
                    <a href="#" className="hover:text-[#00acc1] transition-colors">Conservation</a>
                    <a href="#" className="hover:text-[#00acc1] transition-colors">Research</a>
                </div>
            </nav>

            <main className="relative pt-12 pb-32 overflow-hidden">
                {/* Wave SVG */}
                <div className="absolute top-1/2 left-0 w-full h-screen bg-[#006064] transform -skew-y-3 origin-bottom-left z-0"></div>
                <div className="absolute top-1/2 left-0 w-full h-screen bg-[#00838f] transform -skew-y-6 origin-bottom-right opacity-50 z-0"></div>

                <div className="container mx-auto px-8 relative z-10 flex flex-col md:flex-row items-center gap-16">
                    <div className="md:w-1/2">
                         <div className="inline-flex items-center gap-2 text-cyan-700 font-bold uppercase tracking-widest text-xs mb-6 bg-cyan-100 px-3 py-1 rounded-full">
                             <Waves size={14} /> Marine Biology Focus
                         </div>
                         <h1 className="text-6xl md:text-7xl font-bold text-[#006064] mb-8 leading-tight">
                             Dive Deep Into Knowledge.
                         </h1>
                         <p className="text-xl text-[#00838f] mb-10 leading-relaxed max-w-lg">
                             Explore the blue frontier. Our curriculum flows like water—adaptable, powerful, and life-sustaining.
                         </p>
                         <div className="flex gap-4">
                             <Button className="rounded-full h-14 px-8 bg-[#006064] hover:bg-[#004d40] text-white font-bold shadow-lg">
                                 Apply Now
                             </Button>
                             <Button variant="ghost" className="rounded-full h-14 px-8 text-[#006064] hover:bg-cyan-100 font-bold">
                                 Tour Campus
                             </Button>
                         </div>
                    </div>
                    
                    <div className="md:w-1/2 relative">
                         <div className="relative w-full aspect-square rounded-full border-8 border-white/30 overflow-hidden shadow-2xl animate-float">
                             <img src="https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover" alt="Diver" />
                             <div className="absolute inset-0 bg-[#006064]/20 mix-blend-multiply"></div>
                         </div>
                         
                         {/* Bubble decorations */}
                         <div className="absolute top-0 right-10 w-8 h-8 rounded-full bg-white/40 animate-bounce delay-100"></div>
                         <div className="absolute bottom-20 left-0 w-12 h-12 rounded-full bg-white/40 animate-bounce delay-300"></div>
                    </div>
                </div>
            </main>
            
            <section className="bg-[#004d40] py-20 text-white relative z-10">
                <div className="container mx-auto px-8 text-center">
                    <h2 className="text-3xl font-bold mb-12">Immersive Learning</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                        <div className="p-6 border border-white/10 rounded-2xl hover:bg-white/5 transition-colors">
                            <Droplet size={40} className="mx-auto mb-4 text-cyan-300" />
                            <h3 className="text-xl font-bold mb-2">Water Science</h3>
                            <p className="opacity-70 text-sm">Advanced chemistry and hydrology labs.</p>
                        </div>
                        <div className="p-6 border border-white/10 rounded-2xl hover:bg-white/5 transition-colors">
                            <Fish size={40} className="mx-auto mb-4 text-cyan-300" />
                            <h3 className="text-xl font-bold mb-2">Marine Life</h3>
                            <p className="opacity-70 text-sm">On-campus aquarium and rehabilitation center.</p>
                        </div>
                        <div className="p-6 border border-white/10 rounded-2xl hover:bg-white/5 transition-colors">
                            <Waves size={40} className="mx-auto mb-4 text-cyan-300" />
                            <h3 className="text-xl font-bold mb-2">Oceanography</h3>
                            <p className="opacity-70 text-sm">Understanding the currents that shape our world.</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default OceanicDepths;
